import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type AppLocale } from "./index";

const isSupported = (value: unknown): value is AppLocale =>
  typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);

/**
 * Текущий язык + переключатель. Смена языка применяется мгновенно (i18next →
 * localStorage) и сохраняется в профиль, чтобы переносилась между устройствами
 * и использовалась для писем.
 */
export function useLocale() {
  const { i18n } = useTranslation();
  const setLocaleMutation = trpc.auth.setLocale.useMutation({
    onError: (e) => toast.error(e.message),
  });

  const resolved = (i18n.resolvedLanguage || i18n.language || DEFAULT_LOCALE) as string;
  const locale: AppLocale = isSupported(resolved) ? resolved : DEFAULT_LOCALE;

  const setLocale = useCallback(
    (next: AppLocale) => {
      if (next === locale) return;
      void i18n.changeLanguage(next);
      setLocaleMutation.mutate({ locale: next });
    },
    [i18n, locale, setLocaleMutation],
  );

  return { locale, setLocale, isPending: setLocaleMutation.isPending };
}

/**
 * Один раз после авторизации подтягивает язык из профиля (источник истины при
 * входе), если он расходится с локальным выбором. Рендерится один раз высоко в
 * дереве. Ничего не отображает.
 */
export function LocaleSync() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const syncedRef = useRef(false);

  useEffect(() => {
    if (syncedRef.current) return;
    const serverLocale = (user as { locale?: string } | null)?.locale;
    if (!isSupported(serverLocale)) return;
    syncedRef.current = true;
    if (serverLocale !== (i18n.resolvedLanguage || i18n.language)) {
      void i18n.changeLanguage(serverLocale);
    }
  }, [user, i18n]);

  return null;
}
